<template>
  <div class="all-notification-page">
    <div class="all-notification-header">
      <div>
        <h2 class="all-notification-title">
          All Notifications
        </h2>
        <div class="all-notification-meta">
          {{ unreadCount }} unread
        </div>
      </div>

      <div class="all-notification-actions">
        <b-form-select
          v-model="statusFilter"
          :options="statusOptions"
          class="notification-filter"
        />
        <b-button
          variant="outline-primary"
          :disabled="!unreadCount || isMarkingAll"
          @click="markAllRead"
        >
          {{ isMarkingAll ? 'Updating...' : 'Mark all as read' }}
        </b-button>
      </div>
    </div>

    <b-alert
      v-if="errorMessage"
      show
      variant="danger"
      class="notification-alert"
    >
      <div>{{ errorMessage }}</div>
      <b-button
        size="sm"
        variant="outline-danger"
        @click="fetchNotifications"
      >
        Retry
      </b-button>
    </b-alert>

    <div
      v-if="isLoading"
      class="notification-state"
    >
      <b-spinner
        small
        class="mr-50"
      />
      <span>Loading notifications...</span>
    </div>

    <div
      v-else-if="!notifications.length"
      class="notification-state"
    >
      {{ statusFilter === 'all' ? 'No notifications yet.' : 'No notifications match this filter.' }}
    </div>

    <div
      v-else
      class="notification-list"
    >
      <div
        v-for="item in notifications"
        :key="item.id"
        class="notification-row"
        :class="{ 'is-muted': item.isRead, 'is-loading': readingNotificationId === item.id }"
        @click="markAsRead(item)"
      >
        <div class="notification-left">
          <b-avatar
            v-if="item.avatar"
            :src="item.avatar"
            size="38"
            class="notification-avatar"
          />
          <b-avatar
            v-else-if="item.sourceName !== 'System'"
            :text="item.initials"
            size="38"
            variant="light-secondary"
            class="notification-avatar"
          />
          <b-avatar
            v-else
            size="38"
            variant="light-secondary"
            class="notification-avatar"
          >
            <feather-icon
              :icon="item.icon"
              size="16"
            />
          </b-avatar>

          <div class="notification-text">
            <div class="notification-name">
              {{ item.title }}
            </div>
            <div class="notification-message">
              {{ item.message }}
            </div>
            <div class="notification-time">
              {{ item.timestamp }}
            </div>
          </div>
        </div>

        <div class="notification-right">
          <b-spinner
            v-if="readingNotificationId === item.id"
            small
            label="Loading"
          />
          <span
            v-else-if="!item.isRead"
            class="notification-dot"
          />
          <span
            v-else
            class="notification-read-label"
          >
            Read
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="showPagination"
      class="notification-footer"
    >
      <small>{{ paginationLabel }}</small>
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        size="sm"
        first-number
        last-number
        :disabled="isLoading"
        class="mb-0"
      >
        <template #prev-text>
          Previous
        </template>
        <template #next-text>
          Next
        </template>
      </b-pagination>
    </div>
  </div>
</template>

<script>
import {
  BAvatar, BSpinner, BAlert, BButton, BFormSelect, BPagination,
} from 'bootstrap-vue'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import {
  emitVendorNotificationsUpdated,
  parseVendorNotificationsResponse,
  VENDOR_NOTIFICATIONS_UPDATED_EVENT,
} from '@/utils/vendorNotifications'

const STATUS_OPTIONS = [
  { value: 'all', text: 'All notifications' },
  { value: 'unread', text: 'Unread only' },
  { value: 'read', text: 'Read only' },
]

export default {
  components: {
    BAvatar,
    BSpinner,
    BAlert,
    BButton,
    BFormSelect,
    BPagination,
  },
  data() {
    return {
      readingNotificationId: null,
      notifications: [],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      unreadCount: 0,
      statusFilter: 'all',
      isLoading: false,
      isMarkingAll: false,
      errorMessage: '',
    }
  },
  computed: {
    statusOptions() {
      return STATUS_OPTIONS
    },
    paginationLabel() {
      if (!this.totalRows) return 'Showing 0 entries'

      const start = (this.currentPage - 1) * this.perPage + 1
      const end = start + this.notifications.length - 1

      return `Showing ${start} to ${Math.max(start, end)} of ${this.totalRows} notifications`
    },
    showPagination() {
      return this.totalRows > this.perPage || this.currentPage > 1
    },
  },
  watch: {
    currentPage(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetchNotifications()
      }
    },
    statusFilter() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchNotifications()
      }
    },
  },
  mounted() {
    this.handleNotificationsUpdated = event => {
      const unreadCount = Number(event?.detail?.unreadCount)
      if (!Number.isNaN(unreadCount)) {
        this.unreadCount = unreadCount
      }

      if (event?.detail?.refreshList) {
        this.fetchNotifications()
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener(VENDOR_NOTIFICATIONS_UPDATED_EVENT, this.handleNotificationsUpdated)
    }

    this.fetchNotifications()
    this._notificationPollTimer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        this.fetchNotifications()
      }
    }, 60000)
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener(VENDOR_NOTIFICATIONS_UPDATED_EVENT, this.handleNotificationsUpdated)
    }
    if (this._notificationPollTimer) {
      clearInterval(this._notificationPollTimer)
      this._notificationPollTimer = null
    }
  },
  methods: {
    async fetchNotifications() {
      this.isLoading = true
      this.errorMessage = ''

      try {
        const response = await vendorPanelApi.notifications({
          page: this.currentPage,
          per_page: this.perPage,
          status: this.statusFilter,
        })
        const { notifications, unreadCount, pagination } = parseVendorNotificationsResponse(response)

        this.notifications = notifications
        this.unreadCount = unreadCount
        this.perPage = pagination.perPage
        this.totalRows = pagination.total
      } catch (error) {
        this.notifications = []
        this.totalRows = 0
        this.errorMessage = error?.response?.data?.message || 'Unable to load notifications right now.'
      } finally {
        this.isLoading = false
      }
    },
    async markAsRead(item) {
      if (!item || item.isRead || this.readingNotificationId === item.id) return

      this.readingNotificationId = item.id

      try {
        const response = await vendorPanelApi.notificationRead(item.id)
        const unreadCount = Number(response?.data?.data?.unread_count || Math.max(this.unreadCount - 1, 0))

        this.unreadCount = unreadCount

        if (this.statusFilter === 'unread') {
          if (this.currentPage !== 1 && this.notifications.length === 1) {
            this.currentPage -= 1
          } else {
            await this.fetchNotifications()
          }
        } else {
          item.isRead = true
        }

        emitVendorNotificationsUpdated({ unreadCount, refreshList: true })
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Unable to mark notification as read.'
      } finally {
        this.readingNotificationId = null
      }
    },
    async markAllRead() {
      if (!this.unreadCount || this.isMarkingAll) return

      this.isMarkingAll = true

      try {
        const response = await vendorPanelApi.notificationsMarkAllRead()
        const unreadCount = Number(response?.data?.data?.unread_count || 0)

        this.unreadCount = unreadCount
        emitVendorNotificationsUpdated({ unreadCount, refreshList: true })

        if (this.statusFilter === 'unread') {
          if (this.currentPage !== 1) {
            this.currentPage = 1
          } else {
            await this.fetchNotifications()
          }
        } else {
          this.notifications = this.notifications.map(item => ({
            ...item,
            isRead: true,
          }))
        }
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Unable to mark all notifications as read.'
      } finally {
        this.isMarkingAll = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.all-notification-page {
  .all-notification-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.2rem;
    flex-wrap: wrap;
  }

  .all-notification-title {
    font-size: 2.9rem;
    font-weight: 700;
    color: #13111c;
    margin-bottom: 0.15rem;
  }

  .all-notification-meta {
    color: #8a85a0;
    font-size: 0.92rem;
  }

  .all-notification-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .notification-filter {
    width: 180px;
  }

  .notification-alert {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .notification-state {
    min-height: 12rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    color: #8a85a0;
    background: #fff;
    border: 1px solid #f0eff5;
    border-radius: 6px;
  }

  .notification-list {
    border: 1px solid #f0eff5;
    border-radius: 6px;
    overflow: hidden;
  }

  .notification-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.95rem 1rem;
    border-bottom: 1px solid #f0eff5;
    background: #fff;
  }
  .notification-row.is-loading {
    pointer-events: none;
    opacity: 0.6;
  }

  .notification-row.is-muted {
    background: #f8f8fb;
  }

  .notification-left {
    display: flex;
    align-items: flex-start;
    min-width: 0;
    gap: 0.75rem;
  }

  .notification-avatar {
    flex-shrink: 0;
  }

  .notification-text {
    min-width: 0;
    line-height: 1.2;
  }

  .notification-name {
    color: #2f2b3d;
    font-size: 0.97rem;
    font-weight: 700;
    margin-bottom: 0.2rem;
  }

  .notification-message {
    color: #6f6b86;
    font-size: 0.86rem;
    margin-bottom: 0.2rem;
  }

  .notification-time {
    color: #9a95ad;
    font-size: 0.79rem;
  }

  .notification-right {
    min-width: 2.8rem;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: 0.6rem;
    flex-shrink: 0;
  }

  .notification-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #0a2e57;
  }

  .notification-read-label {
    color: #b8b5c7;
    font-size: 0.8rem;
    font-weight: 600;
  }

  .notification-footer {
    padding: 0.85rem 0.15rem 0;
  }
}
</style>
